/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { withStyles } from '@material-ui/core';
import { useUIDSeed } from 'react-uid';
import {
    bool, string, array, object, shape,
} from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { ChevronDown, ChevronUp } from 'lucide-react';
import GraphqlWineDisclaimer from '../../GraphqlWineDisclaimer/WineDisclaimerContainer';
import { selectedSkuType } from '../../common/types';
import FoodDetailsExpansionPanel from './FoodDetailsExpansionPanel';
import MarketingSpot from '../Partials/MarketingSpot';
import replaceFontWeight from '../../../../../../helpers/replaceFontWeight';
import replaceSpecialChar from '../../../../../../helpers/replaceSpecialChar';

const styles = (theme) => ({
    listContent: {
        fontSize: '16px',
        color: theme.palette.cms?.primary || theme.palette.colorPrimary,
    },
    longDescMarkDown: {
        margin: '0px 16px 0px 0px',
    },
    rootMobile: {
        [theme.breakpoints.up(1025)]: {
            display: 'none',
        },
        marginTop: 20,
        [theme.breakpoints.down(600)]: {
            margin: '20px 0 7px',
        },
    },
    card: {
        marginBottom: '10px',
        background: theme.palette.white,
        boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.12)',
        borderRadius: '4px',
        padding: '16px 16px 0',
        position: 'relative',
    },
    allAvailableContent: {
        height: '100%',
        display: 'inline-block',
        fontSize: '1em',
    },
    trimmedContent: {
        height: '110px',
    },
    toggleDescription: {
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-end',
        position: 'absolute',
        bottom: 0,
        right: '16px',
        backgroundImage: 'linear-gradient(to bottom,rgba(255,255,255,0) 0,rgba(255,255,255,1) 30%)',
    },
    readMore: {
        fontSize: '1.4em',
        margin: '0 3px -4px 0',
    },
    readLess: {
        fontSize: '1.4em',
        margin: '0 3px -4px 0',
    },
    buttonWrapperStatic: {
        position: 'static',
    },
    mobileDivider: {
        borderTop: `1px solid ${theme.palette.grey8 || '#cccccc'}`,
    },
    toggleReadButton: {
        color: theme.palette.cms?.primary || theme.palette.colorPrimary,
        fontSize: '0.8em',
        fontWeight: '700',
        border: 'none',
        cursor: 'pointer',
        background: theme.palette.white,
        lineHeight: '1.5',
        padding: '6px 8px',
        margin: '0 4px 5px',
        borderRadius: '4px',
        '&:focus': {
            outline: 'none',
        },
        '&:hover': {
            textDecoration: 'none',
        },
    },
    header: {
        color: '#000000',
        fontSize: '24px',
        lineHeight: '30px',
        fontFamily: 'LatoBold',
        margin: '20px 0px 12px',
    },
    expansionWrapper: {
        background: '#fafafa',
        padding: '12px 16px 34px',
    },
    detailsRoot: {
        padding: '0px 24px',
        '& li': {
            fontSize: '16px',
            lineHeight: '28px',
        },
    },
    iconMore: {
        textDecoration: 'underline',
        textDecorationThickness: '1px',
        textUnderlineOffset: '3px',
        fontSize: '16px',
        alignSelf: 'flex-start',
        cursor: 'pointer',
        letterSpacing: '0.5px',
        display: 'flex',
        alignItems: 'center',
        lineHeight: '24px',
        marginTop: '12px',
        '& svg': {
            margin: '6px 0px 0px 12px',
            width: '20px',
            height: '20px',
        },
    },
    rootWrapper: {
        background: '#fafafa',
        padding: '32px 32px 32px 16px',
        margin: '20px 0 32px',
        [theme.breakpoints.down('600')]: {
            '& $longDescMarkDown p>strong': {
                fontSize: '16px',
                marginTop: '10px',
                display: 'flex',
            },
        },
    },
    heading: {
        fontSize: '24px',
        lineHeight: '29px',
        paddingBottom: '24px',
        borderBottom: '1px solid #d8d8d8',
    },
});

const GraphqlFoodDetailsUIMobile = ({
    classes, shortDescription, productContentsDescription, longDescription, selectedSku, productContents, clubDescription, isPassportBundleProduct, productAttributes, isPdpRedesignEnabled, productName, partNumber,
}) => {
    const seed = useUIDSeed();
    const [open, close] = useState(true);

    const renderProductContent = () => (productContents && clubDescription ? (
        <ul>
            {
                productContents.map((contents) => (
                    <li key={seed(contents)} className={classes.listContent}>{clubDescription ? <ReactMarkdown source={contents} escapeHtml={false} /> : contents}</li>
                ))
            }
        </ul>
    ) : (
        <div className={classes.longDescMarkDown}>
            {ReactHtmlParser(productContentsDescription || longDescription, {
                transform(node) {
                    replaceFontWeight(node);
                },
            })}
        </div>
    )
    );

    const renderProductDescription = () => (
        <>
            {ReactHtmlParser(shortDescription, {
                transform(node) {
                    replaceFontWeight(node);
                },
            })}
            {!productContentsDescription && (
                <div>{ ReactHtmlParser(longDescription, {
                    transform(node) {
                        replaceFontWeight(node);
                    },
                }) }
                </div>
            )}
            {selectedSku?.ageVerifyFlag === 'true'
                && (
                    <div className={classes.mobileDivider}>
                        <div className={classes.divider} />
                        <GraphqlWineDisclaimer />
                    </div>
                )}
            {selectedSku?.subscriptions?.type === 'Discovery' && (
                <div>
                    <div className={classes.dividerDisclaimer} />
                    <MarketingSpot brandId={selectedSku?.brandId} title="Discovery Subscription Disclaimer" />
                </div>
            )}
        </>
    );

    const renderProductPassport = () => (
        <ReactMarkdown
            source={isPassportBundleProduct?.details?.bundle_details?.long_description}
            escapeHtml={false}
        />
    );

    return (
        <div className={`${classes.rootMobile} ${isPdpRedesignEnabled ? classes.rootWrapper : ''}`}>
            {isPdpRedesignEnabled ? (
                <h2 className={classes.heading}>
                    About {ReactHtmlParser(replaceSpecialChar(productName))}
                </h2>
            ) : ''}
            {(productContentsDescription?.length > 0 || longDescription?.length > 0) && selectedSku?.cyo?.__typename !== 'CYO'
                ? (
                    <FoodDetailsExpansionPanel summary="Contents" details={renderProductContent()} panelId={1} expanded selectedSku={selectedSku} productAttributes={productAttributes} isPdpRedesignEnabled={isPdpRedesignEnabled} partNumber={partNumber} />
                )
                : null}
            <FoodDetailsExpansionPanel summary="Description" details={renderProductDescription()} panelId={2} expanded isPdpRedesignEnabled={isPdpRedesignEnabled} />
            {isPassportBundleProduct?.enable && !isPdpRedesignEnabled && (
                <FoodDetailsExpansionPanel
                    summary={`Celebrations Passport Details: '${isPassportBundleProduct?.details?.item_sku}`}
                    details={renderProductPassport()}
                    panelId={3}
                    expanded={false}
                />
            )}
        </div>
    );
};

GraphqlFoodDetailsUIMobile.propTypes = {
    classes: object.isRequired,
    clubDescription: bool,
    shortDescription: string.isRequired,
    productContents: array.isRequired,
    longDescription: string.isRequired,
    selectedSku: selectedSkuType,
    productContentsDescription: string,
    isPassportBundleProduct: shape({
        enable: bool,
        details: shape({
            bundle_details: shape({
                long_description: string,
            }),
        }),
    }),
    productAttributes: array,
    isPdpRedesignEnabled: bool,
    productName: string,
    partNumber: string,
};

GraphqlFoodDetailsUIMobile.defaultProps = {
    clubDescription: false,
    selectedSku: {},
    productContentsDescription: '',
    isPassportBundleProduct: {},
    productAttributes: [],
    isPdpRedesignEnabled: false,
    productName: '',
    partNumber: '',
};

export default withStyles(styles)(GraphqlFoodDetailsUIMobile);
